import type {
  OrganizationBrandResponse,
  OrganizationSummaryResponse,
} from "~/types/organizations";

export const TEACHPLUS_THEME_COLORS = {
  primary: "#194fff",
  primaryDarken: "#0d3099",
  secondary: "#00c6e0",
  secondaryDarken: "#c4d6f5",
};

const HEX_COLOR_PATTERN = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

export const normalizeHexColor = (
  value: string | null | undefined,
  fallback: string,
) => {
  if (!value || !HEX_COLOR_PATTERN.test(value)) {
    return fallback;
  }

  const normalized = value.toLowerCase();
  if (normalized.length === 4) {
    const [, r, g, b] = normalized;
    return `#${r}${r}${g}${g}${b}${b}`;
  }

  return normalized;
};

export const darkenHexColor = (value: string, ratio = 0.28) => {
  const normalized = normalizeHexColor(value, TEACHPLUS_THEME_COLORS.primary);
  const channels = [1, 3, 5].map((start) =>
    Math.max(
      0,
      Math.round(
        parseInt(normalized.slice(start, start + 2), 16) * (1 - ratio),
      ),
    ),
  );
  return `#${channels
    .map((channel) => channel.toString(16).padStart(2, "0"))
    .join("")}`;
};

export const hexToRgbString = (value: string) => {
  const normalized = normalizeHexColor(value, TEACHPLUS_THEME_COLORS.primary);
  return [1, 3, 5]
    .map((start) => parseInt(normalized.slice(start, start + 2), 16))
    .join(", ");
};

export const buildOrganizationBrand = (
  organization: OrganizationSummaryResponse | null | undefined,
): OrganizationBrandResponse | null => {
  if (!organization?.preferences) {
    return null;
  }

  return {
    organization_id: organization.id,
    slug: organization.slug,
    school_name: organization.school_name,
    logo_url: organization.preferences.logo_url,
    favicon_url: organization.preferences.favicon_url ?? null,
    primary_color: organization.preferences.primary_color,
    secondary_color: organization.preferences.secondary_color,
    white_label_enabled: organization.preferences.white_label_enabled,
    logo_crop: organization.preferences.logo_crop,
    logo_scale_percent: organization.preferences.logo_scale_percent,
    logo_offset_x: organization.preferences.logo_offset_x,
    logo_offset_y: organization.preferences.logo_offset_y,
    header_padding_removed: organization.preferences.header_padding_removed,
  };
};

export const isSchoolBrandActive = (
  brand: OrganizationBrandResponse | null | undefined,
) => Boolean(brand?.white_label_enabled);

export const resolveSchoolBrandPalette = (
  brand: OrganizationBrandResponse | null | undefined,
) => {
  if (!isSchoolBrandActive(brand)) return { ...TEACHPLUS_THEME_COLORS };
  const primary = normalizeHexColor(
    brand?.primary_color,
    TEACHPLUS_THEME_COLORS.primary,
  );
  const secondary = normalizeHexColor(
    brand?.secondary_color,
    TEACHPLUS_THEME_COLORS.secondary,
  );
  return {
    primary,
    secondary,
    primaryDarken: darkenHexColor(primary),
    secondaryDarken: darkenHexColor(secondary),
  };
};
